/**
 * NewStakeholderDialog (Sprint 4.3.8).
 *
 * Modal dialog mirroring the NewPathwayDialog pattern. Captures the
 * three required Stakeholder fields:
 *   - name (free-form, required)
 *   - role (closed enum from `STAKEHOLDER_ROLES`)
 *   - tier (closed enum from `SCRIPS_ROLE_TIERS`, Scrips-distinct per
 *     Phase B `stakeholder-model.md`)
 *   - description (optional, max 280 chars)
 *
 * On submit the dialog calls `useStakeholdersStore.addStakeholder` and
 * forwards the validated stakeholder to `onCreated`. The id is derived
 * deterministically from the name via `slugifyStakeholderName` per HC1
 * reproducibility.
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: dialog uses
 * `role="dialog"` + `aria-modal="true"` + labelled-by; the Cancel
 * button is the first focusable target.
 *
 * Brand-Patch v2: zero `\bTLP[1-4]\b` user-facing.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useStakeholdersStore } from '../../state/stakeholders.store';
import {
  SCRIPS_ROLE_TIERS,
  SCRIPS_ROLE_TIER_LABELS,
  STAKEHOLDER_ROLES,
  STAKEHOLDER_ROLE_LABELS,
  slugifyStakeholderName,
  type ScripsRoleTier,
  type Stakeholder,
  type StakeholderRole,
} from '../../types/stakeholder';

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 280;

export interface NewStakeholderDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called after a successful creation with the validated stakeholder
   * record. The picker auto-selects the new stakeholder.
   */
  onCreated: (stakeholder: Stakeholder) => void;
}

export function NewStakeholderDialog({
  open,
  onClose,
  onCreated,
}: NewStakeholderDialogProps) {
  const titleId = useId();
  const nameId = useId();
  const roleId = useId();
  const tierId = useId();
  const descriptionId = useId();
  const [name, setName] = useState('');
  const [role, setRole] = useState<StakeholderRole>('physician');
  const [tier, setTier] = useState<ScripsRoleTier>('clinical-decision');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const addStakeholder = useStakeholdersStore((s) => s.addStakeholder);

  useEffect(() => {
    if (open) {
      setName('');
      setRole('physician');
      setTier('clinical-decision');
      setDescription('');
      setError(null);
      const t = setTimeout(() => cancelRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isValid = trimmedName.length > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) return;
    const candidate: Stakeholder = {
      id: slugifyStakeholderName(trimmedName),
      name: trimmedName,
      role,
      tier,
      description:
        trimmedDescription.length > 0 ? trimmedDescription : undefined,
    };
    const result = addStakeholder(candidate);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onCreated(result.stakeholder);
  };

  return (
    <div
      data-testid="new-stakeholder-dialog-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/40 p-gutter-md"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="new-stakeholder-dialog"
        className="w-full max-w-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id={titleId}
          className="text-signal-base font-semibold text-text-primary"
        >
          Create a new stakeholder
        </h3>
        <p className="mt-1 text-signal-xs text-text-tertiary">
          Stakeholders represent the role responsible for completing an
          activity. Tier governs the Scrips clinical authorization layer.
        </p>
        <form
          onSubmit={handleSubmit}
          className="mt-gutter-md flex flex-col gap-gutter-md"
        >
          <div className="flex flex-col gap-1">
            <label
              htmlFor={nameId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Name
            </label>
            <input
              id={nameId}
              data-testid="new-stakeholder-dialog-name"
              type="text"
              value={name}
              maxLength={NAME_MAX_LENGTH}
              required
              autoFocus
              onChange={(e) => setName(e.target.value)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={roleId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Role
            </label>
            <select
              id={roleId}
              data-testid="new-stakeholder-dialog-role"
              value={role}
              onChange={(e) => setRole(e.target.value as StakeholderRole)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              {STAKEHOLDER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {STAKEHOLDER_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={tierId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Tier
            </label>
            <select
              id={tierId}
              data-testid="new-stakeholder-dialog-tier"
              value={tier}
              onChange={(e) => setTier(e.target.value as ScripsRoleTier)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              {SCRIPS_ROLE_TIERS.map((t) => (
                <option key={t} value={t}>
                  {SCRIPS_ROLE_TIER_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={descriptionId}
              className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
            >
              Description
            </label>
            <textarea
              id={descriptionId}
              data-testid="new-stakeholder-dialog-description"
              value={description}
              rows={3}
              maxLength={DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
          </div>
          {error !== null ? (
            <p
              role="alert"
              data-testid="new-stakeholder-dialog-error"
              className="text-signal-xs text-signal-danger-500"
            >
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-gutter-sm">
            <button
              ref={cancelRef}
              type="button"
              data-testid="new-stakeholder-dialog-cancel"
              onClick={onClose}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-stakeholder-dialog-submit"
              disabled={!isValid}
              className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-600 focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-60"
            >
              Create stakeholder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
